import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ErrorHandler = ({ 
  isVisible, 
  error, 
  onRetry, 
  onClose 
}) => {
  const getErrorIcon = (errorType) => {
    switch (errorType) {
      case 'network':
        return 'Wifi';
      case 'wallet':
        return 'Wallet';
      case 'screenshot':
        return 'Camera';
      case 'ipfs':
        return 'Database';
      case 'blockchain':
        return 'Shield';
      default:
        return 'AlertCircle';
    }
  };

  const getErrorTitle = (errorType) => {
    switch (errorType) {
      case 'network':
        return 'Network Connection Error';
      case 'wallet':
        return 'Wallet Connection Error';
      case 'screenshot':
        return 'Screenshot Capture Failed';
      case 'ipfs':
        return 'IPFS Upload Failed';
      case 'blockchain':
        return 'Blockchain Transaction Failed';
      default:
        return 'Notarization Error';
    }
  };

  const getErrorSuggestions = (errorType) => {
    switch (errorType) {
      case 'network':
        return [
          'Check your internet connection',
          'Try switching to a different network',
          'Disable VPN if active'
        ];
      case 'wallet':
        return [
          'Ensure MetaMask is installed and unlocked',
          'Switch to Polygon network',
          'Check if wallet is connected to the correct account'
        ];
      case 'screenshot':
        return [
          'Verify the URL is accessible',
          'Check if the webpage loads properly',
          'Try a different URL format'
        ];
      case 'ipfs':
        return [
          'Check network connectivity',
          'Try again in a few moments',
          'Ensure sufficient storage space'
        ];
      case 'blockchain':
        return [
          'Check wallet balance for gas fees',
          'Ensure you\'re on Polygon network',
          'Try increasing gas price'
        ];
      default:
        return [
          'Check your connection and try again',
          'Ensure all requirements are met',
          'Contact support if issue persists'
        ];
    }
  };

  if (!isVisible || !error) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-card border border-border rounded-lg shadow-elevated max-w-md w-full">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center space-x-3">
            <div className="flex items-center justify-center w-10 h-10 bg-destructive/10 rounded-full">
              <Icon name={getErrorIcon(error?.type)} size={20} className="text-destructive" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-foreground">
                {getErrorTitle(error?.type)}
              </h3>
              <p className="text-sm text-muted-foreground">
                Something went wrong during notarization
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="flex items-center justify-center w-8 h-8 text-muted-foreground hover:text-foreground hover:bg-muted rounded-md transition-smooth"
          >
            <Icon name="X" size={16} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-4">
          {/* Error Message */}
          <div className="p-3 bg-destructive/5 border border-destructive/20 rounded-md">
            <p className="text-sm text-destructive font-medium mb-1">Error Details:</p>
            <p className="text-sm text-destructive/80">{error?.message}</p>
          </div>

          {/* Suggestions */}
          <div>
            <h4 className="text-sm font-medium text-foreground mb-2">Troubleshooting Steps:</h4>
            <ul className="space-y-2">
              {getErrorSuggestions(error?.type)?.map((suggestion, index) => (
                <li key={index} className="flex items-start space-x-2">
                  <Icon name="ArrowRight" size={12} className="text-muted-foreground mt-1 flex-shrink-0" />
                  <span className="text-sm text-muted-foreground">{suggestion}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Error Code */}
          {error?.code && (
            <div className="text-center">
              <p className="text-xs text-muted-foreground">
                Error Code: <span className="font-mono">{error?.code}</span>
              </p>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex items-center space-x-3 p-6 border-t border-border">
          <Button
            variant="outline"
            onClick={onClose}
            className="flex-1"
          >
            Cancel
          </Button>
          <Button
            variant="default"
            onClick={onRetry}
            iconName="RotateCcw"
            iconPosition="left"
            className="flex-1"
          >
            Try Again
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ErrorHandler;